import { useTranslation } from '@rhino-project/core/i18n';
import { AuthForm, type AuthFormValues } from '../../components/auth/AuthForm';
import { OmniAuthButtonGroup } from '../../components/buttons/OmniAuthButtonGroup';
import {
  useSignInAction,
  useSignupAllowed,
  useSignInWithPasskey,
  usePasskeyAutoSignIn
} from '@rhino-project/core/queries';
import { AuthPage, type AuthPageProps } from './AuthPage';
import { useRhinoConfig } from '@rhino-project/core/config';
import { NetworkParamError } from '@rhino-project/core/lib';
import {
  Alert,
  AlertContent,
  AlertTitle,
  AlertDescription,
  Button
} from '@heroui/react';
import { Link, useSearch } from '@tanstack/react-router';

export const SignInPage = (props: AuthPageProps) => {
  const { t } = useTranslation();
  const { appName } = useRhinoConfig();
  const { account_confirmation_success } = useSearch({
    strict: false
  }) as { account_confirmation_success?: string };
  const allowSignup = useSignupAllowed();

  const { mutate: loginMutation, isPending, error } = useSignInAction();
  const paramError = error as NetworkParamError | null;
  // The explicit "Sign in with a passkey" button uses the modal ceremony.
  const { mutate: signInWithPasskey, isPending: passkeyPending } =
    useSignInWithPasskey();
  // On mount, passively offer passkeys: Chrome's immediate picker where
  // supported, conditional-UI autofill (Safari, etc.) otherwise.
  usePasskeyAutoSignIn();

  const confirmed = account_confirmation_success === 'true';

  const handleSubmit = (formValues: AuthFormValues) =>
    loginMutation(formValues);

  const authDesc = t(
    'rhino.frontend.messages.enter_your_email_and_password_to_sign_in'
  );

  return (
    <AuthPage
      title={t('rhino.frontend.messages.sign_in_to_your_account')}
      description={authDesc}
      {...props}
    >
      {confirmed && (
        <Alert
          status="success"
          className="mb-4 bg-success/10 border border-success/20"
        >
          <AlertContent>
            <AlertTitle>
              {t('rhino.frontend.messages.account_confirmed')}
            </AlertTitle>
            <AlertDescription>
              {t(
                'rhino.frontend.messages.your_account_has_been_confirmed_please_sign_in'
              )}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
      <AuthForm
        emailField
        webauthnAutofill
        passwordField
        primaryAction={t('rhino.frontend.messages.sign_in')}
        secondaryAction={{
          content: t('rhino.frontend.messages.forgot_password'),
          url: '/auth/reset-password'
        }}
        isPending={isPending}
        errors={paramError?.errors}
        onSubmit={handleSubmit}
        {...props}
      />
      <Button
        className="mt-2 w-full"
        variant="outline"
        isDisabled={passkeyPending}
        onPress={() => signInWithPasskey(undefined)}
      >
        {t('rhino.frontend.messages.sign_in_with_a_passkey')}
      </Button>
      <OmniAuthButtonGroup />
      {Boolean(allowSignup) && (
        <p className="text-sm text-muted text-center mt-4">
          {t('rhino.frontend.messages.new_to_app', { appName })}{' '}
          <Link
            className="text-accent hover:underline"
            to="/auth/signup"
            search={{ redirect: undefined }}
          >
            {t('rhino.frontend.messages.sign_up')}
          </Link>
        </p>
      )}
    </AuthPage>
  );
};
